import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Filter, X, Pencil, Save, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { hasPermission, isAdmin } from '@/utils/permissions';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DailyDemandView = () => {
  const [demands, setDemands] = useState([]);
  const [filteredDemands, setFilteredDemands] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState(0);
  const [filters, setFilters] = useState({
    date: '',
    customer_id: 'all',
    item_id: 'all',
    qty_min: '',
    qty_max: ''
  });
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, demand: null });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('demand_date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    fetchDemands();
  }, [pagination.page, sortBy, sortOrder, filters]);

  const fetchMasterData = async () => {
    try {
      const [custRes, itemRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/items`)
      ]);
      setCustomers(custRes.data);
      setItems(itemRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchDemands = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add server-side filters
      if (filters.date) params.append('date', filters.date);
      if (filters.customer_id && filters.customer_id !== 'all') params.append('customer_id', filters.customer_id);
      if (filters.item_id && filters.item_id !== 'all') params.append('item_id', filters.item_id);
      
      const response = await axios.get(`${API}/schedule/daily?${params.toString()}`);
      
      if (response.data.data) {
        setDemands(response.data.data);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        }));
      } else {
        setDemands(response.data);
      }
    } catch (error) {
      console.error('Error fetching demands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Apply only client-side qty filters (server handles date, customer, item filters)
  useEffect(() => {
    let filtered = [...demands];

    // Client-side qty range filters only
    if (filters.qty_min) {
      filtered = filtered.filter(d => d.qty >= parseFloat(filters.qty_min));
    }

    if (filters.qty_max) {
      filtered = filtered.filter(d => d.qty <= parseFloat(filters.qty_max));
    }

    setFilteredDemands(filtered);
  }, [demands, filters.qty_min, filters.qty_max]);

  const clearFilters = () => {
    setFilters({
      date: '',
      customer_id: 'all',
      item_id: 'all',
      qty_min: '',
      qty_max: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const startEdit = (demand) => {
    setEditingId(demand.id);
    setEditValue(demand.qty);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue(0);
  };

  const saveEdit = async (demandId) => {
    if (editValue <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    try {
      await axios.put(`${API}/schedule/daily/${demandId}`, { qty: editValue });
      toast.success('Demand quantity updated successfully');
      setEditingId(null);
      fetchDemands();
    } catch (error) {
      toast.error('Failed to update demand');
    }
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || id;
  const getItemDesc = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return id;
    return `${item.part_number} - ${item.description}`;
  };

  const handleDeleteDemand = async () => {
    if (!deleteDialog.demand) return;
    
    try {
      await axios.delete(`${API}/daily-demand/${deleteDialog.demand.id}`);
      toast.success('Daily demand deleted successfully');
      setDeleteDialog({ open: false, demand: null });
      fetchDemands();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete demand';
      toast.error(errorMsg);
    }
  };

  const hasActiveFilters = filters.date !== '' || 
    (filters.customer_id !== '' && filters.customer_id !== 'all') || 
    (filters.item_id !== '' && filters.item_id !== 'all') || 
    filters.qty_min !== '' || 
    filters.qty_max !== '';

  return (
    <Card data-testid="daily-demand-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>Daily Demand Schedule (Editable)</CardTitle>
              <CardDescription>Auto-generated from monthly schedules - Edit quantities based on customer voice</CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => fetchDemands()} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Section */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-600" />
            <h3 className="font-semibold text-sm text-slate-700">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Date</label>
              <Input
                data-testid="filter-date"
                type="date"
                placeholder="Filter by date"
                value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Customer</label>
              <Select
                data-testid="filter-customer"
                value={filters.customer_id}
                onValueChange={(value) => setFilters({ ...filters, customer_id: value })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Item (FG Only)</label>
              <SearchableItemSelect
                items={items.filter(i => {
                  if (i.category !== 'FG') return false;
                  if (filters.customer_id && filters.customer_id !== 'all') {
                    return i.customer_id === filters.customer_id || !i.customer_id;
                  }
                  return true;
                })}
                value={filters.item_id}
                onValueChange={(value) => setFilters({ ...filters, item_id: value })}
                placeholder="All FG items"
                dataTestId="filter-item"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Qty Min</label>
              <Input
                data-testid="filter-qty-min"
                type="number"
                placeholder="Min qty"
                value={filters.qty_min}
                onChange={(e) => setFilters({ ...filters, qty_min: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-slate-600">Qty Max</label>
              <Input
                data-testid="filter-qty-max"
                type="number"
                placeholder="Max qty"
                value={filters.qty_max}
                onChange={(e) => setFilters({ ...filters, qty_max: e.target.value })}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Customer Voice Flexibility:</strong> Edit quantities directly based on customer requests. Changes will be reflected when you run MRP.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : filteredDemands.length === 0 ? (
          <div data-testid="no-demands" className="text-center py-8 text-slate-500">
            {hasActiveFilters ? 'No demands match the selected filters.' : 'No daily demands yet. Upload monthly schedules first.'}
          </div>
        ) : (
          <div>
            <div className="mb-2 text-sm text-slate-600">
              Showing <strong>{filteredDemands.length}</strong> of <strong>{demands.length}</strong> records
            </div>
            <div className="overflow-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Item Code - Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    {hasPermission('demand', 'edit') && (
                      <TableHead className="text-right">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDemands.map((demand) => {
                    const item = items.find(i => i.id === demand.item_id);
                    return (
                    <TableRow key={demand.id} data-testid={`demand-row-${demand.id}`}>
                      <TableCell className="font-medium">{demand.demand_date}</TableCell>
                      <TableCell>{getCustomerName(demand.customer_id)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-mono text-blue-700 font-medium">{item?.part_number || 'N/A'}</span>
                          <span className="text-slate-500 ml-2">{item?.description || ''}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {editingId === demand.id ? (
                          <Input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(parseFloat(e.target.value))}
                            className="w-24 h-8 text-right"
                            autoFocus
                          />
                        ) : (
                          <span className="font-semibold">{demand.qty}</span>
                        )}
                      </TableCell>
                      {hasPermission('demand', 'edit') && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {editingId === demand.id ? (
                              <>
                                <Button size="sm" onClick={() => saveEdit(demand.id)} className="h-8">
                                  <Save className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit} className="h-8">
                                  <X className="w-3 h-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => startEdit(demand)} data-testid={`edit-demand-${demand.id}`}>
                                  <Pencil className="w-4 h-4 text-blue-600" />
                                </Button>
                                {isAdmin() && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => setDeleteDialog({ open: true, demand })}
                                    data-testid={`delete-demand-${demand.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            </div>
          </div>
        )}
      </CardContent>

      {/* Delete Demand Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, demand: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Daily Demand</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this daily demand entry?
              {deleteDialog.demand && (
                <div className="mt-2 p-2 bg-slate-100 rounded text-sm">
                  <strong>Date:</strong> {deleteDialog.demand.demand_date}<br />
                  <strong>Item:</strong> {getItemDesc(deleteDialog.demand.item_id)}<br />
                  <strong>Quantity:</strong> {deleteDialog.demand.qty}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDemand}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default DailyDemandView;
